import { BankAccountRequestDto, BankAccountResponseDto, IdentityVerificationResponseDto } from '@repo/services/hooks';
import { batchRequestsOf } from '@repo/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';

import http from 'services/http';
import { LOADING_KEY, withLoading } from 'utils/promise';
import { createThrottledRequest } from 'utils/time';

import { queryClient } from 'components/provider/GlobalProvider';

export const VERIFICATION_KEY = {
  user: ['usersIdentityVerifications'],
  // bankAccount: (isMasked: boolean) => ['bankAccountVerifications', { isMasked }],
  bankAccount: ['bankAccountVerifications'],
  bankList: ['bankList'],
};

export const useGetUsersIdentityVerifications = (config?: AxiosRequestConfig) => {
  return useQuery({
    queryKey: VERIFICATION_KEY.user,
    queryFn: () =>
      withLoading(LOADING_KEY.ID_VERIFICATIONS, () =>
        http.get<IdentityVerificationResponseDto>(`/my/user-identity-verifications`, {
          useLoading: false,
          useErrorToast: false,
          ...config,
        })
      ),
    staleTime: Infinity,
    gcTime: Infinity,
    throwOnError: false,
  });
};

export const useCreateUsersIdentityVerification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (identityVerificationId: string) =>
      http.post(`/user-identity-verifications/ko-kyc`, { identityVerificationId }, { useErrorToast: false }),
    throwOnError: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['usersIdentityVerifications'] }),
  });
};

export const prefetchUnmaskedUserValue = async (userId: string) => {
  await queryClient.prefetchQuery({
    // queryKey: VERIFICATION_KEY.bankAccount(false),
    queryKey: VERIFICATION_KEY.bankAccount,
    queryFn: () =>
      http.get<BankAccountResponseDto>(
        `/users/${userId}/bank-account-verifications`,
        { isMasked: false },
        {
          useLoading: false,
          useErrorToast: false,
        }
      ),
    staleTime: 1000 * 60 * 3,
  });
};

export const useGetBankAccountVerifications = (
  userId?: string,
  isBankAccountVerificationEnabled?: boolean
  // queryParams?: { isMasked: boolean }
) => {
  // const _queryParams = filterObject<Object>(queryParams ?? { isMasked: true });

  return useQuery({
    // queryKey: VERIFICATION_KEY.bankAccount(queryParams?.isMasked ?? true),
    queryKey: VERIFICATION_KEY.bankAccount,
    queryFn: () =>
      withLoading(LOADING_KEY.BANK_VERIFICATIONS, () =>
        http.get<BankAccountResponseDto>(
          `/users/${userId}/bank-account-verifications`,
          {},
          {
            useLoading: false,
            useErrorToast: false,
          }
        )
      ),
    throwOnError: false,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: Boolean(userId) || Boolean(isBankAccountVerificationEnabled),
  });
};

export const useCreateBankAccountVerification = (userId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BankAccountRequestDto) =>
      http.post(`/users/${userId}/bank-account-verifications`, data, { useErrorToast: false }),
    throwOnError: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VERIFICATION_KEY.bankAccount }),
  });
};

// 중복 호출 처리 예정

const postWithdrawalsRequest = batchRequestsOf(
  createThrottledRequest(
    async (data: Pick<BankAccountRequestDto, 'bankCode' | 'accountNumber'>, userId?: string) =>
      http.put(`/users/${userId}/bank-account-verifications`, data, { useErrorToast: false }),
    2000,
    {
      leading: true,
      trailing: false,
    }
  )
);
export const useUpdateBankAccountVerification = (userId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Pick<BankAccountRequestDto, 'bankCode' | 'accountNumber'>) =>
      postWithdrawalsRequest(data, userId),
    throwOnError: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VERIFICATION_KEY.bankAccount });
    },
  });
};
// export const useUpdateBankAccountVerification = (userId?: string) => {
//   const queryClient = useQueryClient();
//   return useMutation(
//     (data: Pick<BankAccountRequestDto, 'bankCode' | 'accountNumber'>) =>
//       http.put(`/users/${userId}/bank-account-verifications`, data, { useErrorToast: false }),
//     {
//       throwOnError: false,
//       onSuccess: () => {
//         queryClient.invalidateQueries({ queryKey: VERIFICATION_KEY.bankAccount });
//       },
//     }
//   );
// };

export type BankListResponseDto = Array<{ code: string; name: string }>;

const fetchBankList = () =>
  http.get<BankListResponseDto>('/bank-list', {}, { useLoading: false, useErrorToast: false });

export const prefetchBankList = async () => {
  await queryClient.prefetchQuery({
    queryKey: VERIFICATION_KEY.bankList,
    queryFn: fetchBankList,
    staleTime: 1000 * 60,
  });
};
export const useGetBankList = (isBankAccountVerificationEnabled: boolean) => {
  return useQuery({
    queryKey: VERIFICATION_KEY.bankList,
    queryFn: () => http.get<BankListResponseDto>('/bank-list', {}, { useLoading: false, useErrorToast: false }),
    throwOnError: false,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: isBankAccountVerificationEnabled,
  });
};
